/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Monster from "model/monster/Monster"
import Attributes from "model/stats/Attributes";
import AttributeValues from "model/stats/AttributeValues";

export const italicizeAttackPhrase = (ability: Ability, mon: Monster) => {
    const allAttrByModifier = new Map<number, Set<keyof AttributeValues>>();
    for (const attr of Attributes.attributes) {
        const attLevel: number = mon.attributes[attr];
        const modifier: number = Attributes.getBonus(attLevel);
        // Skip 0-modifier attributes: a damage roll with no flat bonus shouldn't be inferred
        // as "uses this attribute" just because that attribute happens to have a +0 modifier.
        if (modifier === 0) continue;
        if (!allAttrByModifier.has(modifier)) {
            allAttrByModifier.set(modifier, new Set());
        }
        allAttrByModifier.get(modifier)?.add(attr);
    }
    // this will contain only the entries that have 1 value
    const attrByModifier = new Map<number, keyof AttributeValues>();
    for (const entry of allAttrByModifier.entries()) {
        const attrs: Set<keyof AttributeValues> = entry[1];
        if (attrs.size === 1) {
            const attr: keyof AttributeValues | undefined = attrs.values().next().value;
            if (attr)
                attrByModifier.set(entry[0], attr);
        }
    }

    let indexOfHit = 100;
    // In case of attacks
    if (ability.desc.toLowerCase().includes(" attack:") ||
        ability.desc.toLowerCase().includes(" attack roll:")) { // 2024 format

        const attackTypes = [
            "melee or ranged weapon attack:",
            "melee or ranged spell attack:",
            "melee weapon attack:",
            "melee spell attack:",
            "ranged weapon attack:",
            "ranged spell attack:",
            "melee attack roll:",            // 2024 format
            "ranged attack roll:",           // 2024 format
            "melee or ranged attack roll:"   // 2024 format
        ];

        // Italicize the correct parts of attack-type actions
        let lowercaseDesc = ability.desc.toLowerCase();
        let attackType = null;
        for (const type of attackTypes) {
            if (lowercaseDesc.startsWith(type) || lowercaseDesc.startsWith("_" + type + "_")) {
                attackType = type;
                break;
            }
            if (lowercaseDesc.startsWith(type) || lowercaseDesc.startsWith("_" + type + "_")) {
                attackType = "_" + type + "_";
                break;
            }
        }
        if (attackType == null) {
            return;
        }

        if (!attackType.startsWith("_")) {
            ability.desc = "_" + ability.desc.slice(0, attackType.length) + "_" +
                                ability.desc.slice(attackType.length);
        }
        lowercaseDesc = ability.desc.toLowerCase();
        indexOfHit = lowercaseDesc.indexOf(" hit:");
        if (indexOfHit > 0) {
            // Add one to skip over the space at start of " hit:"
            indexOfHit++;
            ability.desc = ability.desc.slice(0, indexOfHit) + "_" +
                           ability.desc.slice(indexOfHit, indexOfHit + "hit:".length) + "_" +
                           ability.desc.slice(indexOfHit + "hit:".length);
            lowercaseDesc = ability.desc.toLowerCase();
        } else {
            // check for an exist "_hit:_", so we don't add another one (Black Flag monsters come with this format)
            indexOfHit = lowercaseDesc.indexOf(" _hit:_");
        }

        const attrToHitFunc = (mon: Monster, attribute: string) => {
            const toHit = Attributes.getBonus(mon.attributes[attribute.toLowerCase() as keyof AttributeValues])
                        + mon.proficiencyBonus;
            return ((toHit > 0) ? '+' : '') + toHit;
        }
        const toHitRegEx = / ([+-]?\s?\d+)(\s+to hit\b)/i;
        let match = toHitRegEx.exec(ability.desc);
        let prefix = '';
        if (!match) {
            // check for the 2024 format, such as: _Melee Attack Roll:_ +4, reach 5 feet. 4 (1d4 + 2) Slashing damage.
            prefix = ' Attack Roll:_ ';
            const toHitRegEx2024 = /( Attack Roll:_ [+-]?\s?\d+)(, (reach|range))/i;
            match = toHitRegEx2024.exec(ability.desc);
            if (match && match.length > 1) {
                match[1] = match[1].substring(prefix.length);
            }
        }
        if (match) {
            let attributesToConsider = ['Strength', 'Dexterity'];
            if (attackType.includes('spell attack')) {
                attributesToConsider = ['Intelligence', 'Wisdom', 'Charisma'];
            }
            const toHit = match[1];
            for (const attr of attributesToConsider) {
                const attrToHit = attrToHitFunc(mon, attr);
                if (toHit == attrToHit && (!ability.attribute || (ability.attribute == attr))) {
                    const newToHit = ` [${attr.substring(0, 3).toUpperCase()} ATK]`;
                    ability.attribute = attr;
                    ability.desc = ability.desc.replace(match[0], prefix + newToHit + match[2]);
                    lowercaseDesc = ability.desc.toLowerCase();
                    break;
                }
            }
        }
    }
    const damageRegEx = / \d+ \((\d+d\d+) ?([+-]? ?\d*)\)( [a-z]* damage)?/i;
    let dmgMatch = damageRegEx.exec(ability.desc);
    // indexOfHit will only be <0 if this is an attack from the 'if' block above, and there was no "hit:" text.
    let hitNeeded = (indexOfHit < 0);
    let attrBonus = ability.attribute ? Attributes.getBonus(mon.attributes[ability.attribute.toLowerCase() as keyof AttributeValues]) : 0;
    while (dmgMatch) {
        const dmgDice = dmgMatch[1];
        const dmgDiceBonus = parseInt((dmgMatch[2] || '0').replace(/ /g, ''));
        if (dmgDiceBonus === 0 && attrBonus !== 0) {
            // This is canned dice, not using the attribute's bonus:
            const newDamage = ` [${dmgDice}]${dmgMatch[3] || ''}`;
            ability.desc = ability.desc.replace(dmgMatch[0], newDamage);
        } else {
            // Decide what (if any) attribute to prefix on this specific damage roll.
            // - If ability.attribute is set AND this damage's bonus exactly matches its modifier,
            //   it's the primary attribute-based damage: prefix with the attribute.
            // - If ability.attribute is set but this damage's bonus DOESN'T match (e.g. secondary
            //   "+2 fire damage" on a STR-based attack), don't prefix — show the raw bonus.
            // - If ability.attribute isn't set, try inference from the unique-modifier map.
            let prefix = '';
            let displayBonus: number = dmgDiceBonus;
            if (ability.attribute && dmgDiceBonus === attrBonus) {
                prefix = ability.attribute.substring(0, 3).toUpperCase() + ' ';
                displayBonus = 0;
            } else if (!ability.attribute) {
                const singleAttr: keyof AttributeValues | undefined = attrByModifier.get(dmgDiceBonus);
                if (singleAttr) {
                    prefix = singleAttr.substring(0, 3).toUpperCase() + ' ';
                    displayBonus = 0;
                }
            }
            const bonusAttr = (displayBonus > 0) ? (' + ' + displayBonus) : ((displayBonus < 0) ? (' - ' + -displayBonus) : '');
            let newDamage = ' [' + prefix;
            newDamage += dmgDice + bonusAttr + ']';
            if (hitNeeded && (dmgMatch[3] != null)) {
                hitNeeded = false;
                newDamage = ' _Hit:_ ' + newDamage;
            }
            ability.desc = ability.desc.replace(dmgMatch[0], newDamage + (dmgMatch[3] || ''));
        }
        dmgMatch = damageRegEx.exec(ability.desc);
    }
    return ability.desc;
}
